import { useTranslation } from 'react-i18next';
import { useEffect, useRef, useState } from "react";
import './index.css'
import TwinPane from "../TwinPane";
import { pTranslationArray } from '../../helpers/Helpers';
import { ActionTypes, AnnotationTypes } from '@smarterservices/twilio-sync-client';
import { config } from '../../config';
import * as icons from '../Icons';

export default function RoomScan(props) {
  const { t: translation } = useTranslation();
  const [recording, setRecording] = useState(false);
  const [finished, setFinished] = useState(false);
  const [deviceId, setDeviceId] = useState(null);
  const [ videoPaused, setVideoPaused ] = useState(true);
  const [ checkboxDisabled, setCheckboxDisabled ] = useState(true);
  const [ continueDisabled, setContinueDisabled ] = useState(true);
  const [ videoWatched, setVideoWatched ] = useState(false);
  const [ videoStarted, setVideoStarted ] = useState(false);
  const videoStream = useRef(null);
  const {syncClient, setNextEnabled} = props;
  const actionTypes = new ActionTypes();

  const startWebcamStream = () => {
    syncClient.addAction(actionTypes.session.createAnnotation(AnnotationTypes.roomScanStart()));
    const constraints = {
      video: { deviceId },
      audio: false
    }
    navigator.mediaDevices.getUserMedia(constraints)
    .then(stream => {
      videoStream.current = stream;
      const video = document.getElementById('video');
      video.srcObject = stream;
      video.onloadedmetadata = () => {
          video.play()
        }
    })
  }

  const stopWebcamStream = () => {
    syncClient.addAction(actionTypes.session.createAnnotation(AnnotationTypes.roomScanStop()));
    const tracks = videoStream.current.getTracks();

    tracks.forEach(track => track.stop());
    videoStream.current = null;
  }

  const handleVideo = () => {
    const video = document.getElementById('room-scan-video');
    
    if (video.paused) {
      setVideoPaused(false);
      setVideoStarted(true);
      video.play();
    } else {
      setVideoPaused(true);
      video.pause();
    }
  }

  const handleCheckBox = (checked) => {
    if (checked) {
      setContinueDisabled(false);
    } else {
      setContinueDisabled(true);
    }
  }

  const seekBack = () => {
    const video = document.getElementById('room-scan-video');

    if (video.currentTime > 10) {
      video.currentTime = video.currentTime - 10;
    } else {
      video.currentTime = 0;
    }
  }

  const restartVideo = () => {
    const video = document.getElementById('room-scan-video');
    setVideoStarted(false);
    video.currentTime = 0;
    handleVideo();
  }

  const recordingButton = () => {
    if (recording) {
      return (
        <button
            className='btn'
            onClick={() => {
                setFinished(true);
                setRecording(false);
                stopWebcamStream();
              }
            }
          >
            {translation('room_scan.buttons.stop_recording')}
          </button>
      )
    } else if (finished) {
      return (
        <button
            className='btn'
            onClick={() => {
                setFinished(false);
              }
            }
          >
            {translation('room_scan.buttons.retake')}
          </button>
      )
    } else {
      return (
        <button
            className='btn'
            onClick={() => {
                setRecording(true);
                startWebcamStream();
              }
            }
          >
            {translation('room_scan.buttons.start_recording')}
          </button>
      )
    }
  }

  useEffect(()=> {
    setNextEnabled(finished)
  })

  useEffect(() => {
    const getSyncDoc = async () => {
      if(syncClient) {
        const syncDoc = await syncClient.getSync();
        let videoDeviceObj = {};
        try {
            videoDeviceObj = JSON.parse(syncDoc.videoDeviceId);
        } catch (error) {
            videoDeviceObj.deviceId = '';
            console.error('Error parsing JSON:', error);
        }
        setDeviceId(videoDeviceObj?.deviceId);
      }
    }

    getSyncDoc()
  }, [syncClient])

  useEffect(() => {
    syncClient.setSync({currentStep: "RoomScan"});
    //eslint-disable-next-line
  },[])

  const renderLeftPane = () => {
    if (props.userData.session.mandatoryRoomScanVideo && !videoWatched) {
      return (
        <div className='body'>
          <p>{translation('room_scan.scan_required')}</p>
          <h2>{translation('room_scan.instruction.header')}</h2>
          <p>{translation('room_scan.instruction.content')}</p>
        </div>
      )
    }
    return (
      <div className="body">
        {pTranslationArray(translation('room_scan.left_pane.content'))}

        <h2>{translation('room_scan.left_pane.steps.0')}</h2>
        {pTranslationArray(translation('room_scan.left_pane.step_1'))}

        <h2>{translation('room_scan.left_pane.steps.1')}</h2>
        {pTranslationArray(translation('room_scan.left_pane.step_2'))}

        <h2>{translation('room_scan.left_pane.steps.2')}</h2>
        {pTranslationArray(translation('room_scan.left_pane.step_3'))}

      </div>
    )
  }
  const renderRightPane = () => {
    if (props.userData.session.mandatoryRoomScanVideo && !videoWatched) {
      return (
        <div className='recording column'>
          <figure className='room-scan--figure'>
            {videoPaused &&
              <span
                className='play-btn'
                onClick={handleVideo}
              >
                <icons.iconPlay />
              </span>
            }
            <video
              id='room-scan-video'
              type='video/mp4'
              onClick={handleVideo}
              onEnded={() => setCheckboxDisabled(false)}
              onError={() => setVideoWatched(true)} // If we can't load the video for some reason skip the video section
            >
              <source src={config.roomScanVideoUrl}></source>
            </video>

            {(videoStarted && checkboxDisabled) && <button className='btn' onClick={seekBack}>{translation('room_scan.rewind')}</button>}
            {(videoStarted && !checkboxDisabled) && <button className='btn' onClick={restartVideo}>{translation('room_scan.restart')}</button>}
          </figure>
          {!checkboxDisabled &&
            <div className='room-scan--controls'>
              <div className='room-scan--checkbox'>
                <input type='checkbox' id='room-scan-check' disabled={checkboxDisabled} onClick={(event) => handleCheckBox(event.target.checked)} />
                <label for='room-scan-check'>{translation('room_scan.checkbox_label')}</label>
              </div>
              <button
                className='btn'
                disabled={continueDisabled}
                onClick={() => setVideoWatched(true)}
              >
                {translation('room_scan.continue')}
              </button>
            </div>
          }
        </div>
      )
    }
    return (
      <div className="recording column">
        {recording &&
        <figure className='webcam'>
          <video id='video' />

          {recordingButton()}
          </figure>
        }
        {finished &&
            <div className='column room-scan'>
              <icons.iconCheckCircle />
              <h2>{translation('room_scan.scan_successful')}</h2>
            </div>
        }
        {!recording && recordingButton()}

      </div>
    )
  }


  return (
    <>
      <TwinPane
        leftPane={renderLeftPane()}
        rightPane={renderRightPane()}
      ></TwinPane>
    </>
  )

}